import { Controller } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { Select } from "ui/select";
import { Switch } from "ui/switch";
import { TextField } from "ui/text-field";
import CreativeFormFields from "components/modals/creative-modal/components/creative-form-fields";

import { useAddEmail } from "components/modals/creative-modal/hooks/use-add-email";
import { getFYOptions } from "utilities";

import Icons from "assets";

interface AddEmailModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onConfirmClose?: () => void;
  folderId?: string;
}

const AddEmailModal = ({ isOpen, onClose, onConfirmClose, folderId }: AddEmailModalProps) => {
  const {
    methods,
    isFormValid,
    submitState,
    handleAddEmailSubmit,
    handleCancelClick,
    contentTypes,
    contentTypeOptions,
    emailTemplateOptions,
    isLoadingEmailTemplates,
    classTypeOptions,
    populationTypeOptions,
  } = useAddEmail(isOpen, onClose, onConfirmClose, folderId);

  const { control, watch } = methods;
  const fyOptions = getFYOptions();
  const selectedEmailTemplateId = watch("emailTemplateId");
  const hasTemplateSelected = !!selectedEmailTemplateId;

  if (!isOpen) return null;

  return (
    <FormDialog
      open
      onClose={handleCancelClick}
      icon={<Icons.Email />}
      headline="Create email"
      form={methods}
      onSubmit={handleAddEmailSubmit}
      submitDisabled={!isFormValid}
      submitting={submitState === "loading"}
      submitButtonText="Create email"
      submitLoadingMessage="Creating email...">
      <Controller
        name="emailTemplateId"
        control={control}
        render={({ field: { value, onChange, ...field } }) => (
          <Select
            {...field}
            label="Email template"
            selectName="email-template-select"
            disabled={isLoadingEmailTemplates}
            options={emailTemplateOptions}
            value={emailTemplateOptions?.find((option) => option.value === value) ?? null}
            onChange={(selected) => onChange(selected?.value ?? "")}
            ariaLabel="Select email template"
            startIcon={<Icons.Search className="text-aqua-600" />}
          />
        )}
      />
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            label="Email name"
            required
            errMessage={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <CreativeFormFields
        populationTypeOptions={populationTypeOptions}
        contentTypeOptions={contentTypeOptions}
        classTypeOptions={classTypeOptions}
        fyOptions={fyOptions}
        isContentTypeLocked={contentTypes?.length === 1}>
        <Controller
          name="applyClientTheme"
          control={control}
          defaultValue={true}
          render={({ field }) => (
            <div className="flex items-center gap-3">
              <Switch
                toggleName="Enable client theme"
                checked={field.value ?? true}
                onChange={field.onChange}
                disabled={hasTemplateSelected}
              />
              {hasTemplateSelected && (
                <span className="text-sm text-gray-900">Theme set by template</span>
              )}
            </div>
          )}
        />
      </CreativeFormFields>
    </FormDialog>
  );
};

export default AddEmailModal;
